import React, { useEffect, useState } from 'react';
import { User as UserIcon, Loader2, Save, KeyRound, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { MEMBER_NAV } from '../../constants/nav';
import { useAuth } from '../../features/auth/AuthContext';
import {
  updateProfile,
  changePassword,
  getEmailPreferences,
  updateEmailPreferences,
  type EmailPreferences,
} from '../../features/profile/api/profile';
import { PROFILE } from '../../constants/testIds';

const inputClass = 'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

export default function ProfilePage() {
  const { user, refreshUser } = useAuth();
  const [fullName, setFullName] = useState(user?.full_name || '');
  const [phone, setPhone] = useState(user?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);

  const [prefs, setPrefs] = useState<EmailPreferences | null>(null);
  const [savingPrefs, setSavingPrefs] = useState(false);

  useEffect(() => {
    getEmailPreferences().then(setPrefs).catch(() => setPrefs({ signal: true, broadcast: true, promo: true }));
  }, []);

  const togglePref = async (key: keyof EmailPreferences) => {
    if (!prefs) return;
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setSavingPrefs(true);
    try {
      const saved = await updateEmailPreferences(next);
      setPrefs(saved);
      toast.success('Preferensi email tersimpan');
    } catch (e: any) {
      setPrefs(prefs); // revert
      toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message });
    } finally {
      setSavingPrefs(false);
    }
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await updateProfile({ full_name: fullName, phone });
      await refreshUser();
      toast.success('Profil diperbarui');
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSavingProfile(false); }
  };

  const savePassword = async () => {
    if (newPw !== confirmPw) { toast.error('Konfirmasi password tidak cocok'); return; }
    if (newPw.length < 6) { toast.error('Password minimal 6 karakter'); return; }
    setSavingPw(true);
    try {
      await changePassword(currentPw, newPw);
      toast.success('Password berhasil diubah');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
    finally { setSavingPw(false); }
  };

  return (
    <DashboardLayout navItems={MEMBER_NAV} title="Profil">
      <div className="max-w-2xl" data-testid={PROFILE.page}>
        <div className="mb-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center"><UserIcon className="w-5 h-5 text-cyan-400" strokeWidth={1.5} /></div>
          <div><p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Akun</p><h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">Profil Saya</h2></div>
        </div>

        <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6">
          <h3 className="text-sm uppercase tracking-wider text-white/70 mb-4">Informasi Akun</h3>
          <div className="space-y-3">
            <div><label className={labelClass}>Email</label><input className={`${inputClass} opacity-60 cursor-not-allowed`} value={user?.email || ''} disabled /></div>
            <div><label className={labelClass}>Nama Lengkap</label><input data-testid={PROFILE.fullName} className={inputClass} value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
            <div><label className={labelClass}>Nomor HP</label><input data-testid={PROFILE.phone} className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="08xxxxxxxxxx" /></div>
          </div>
          <button data-testid={PROFILE.saveBtn} onClick={saveProfile} disabled={savingProfile || !fullName || !phone} className="mt-5 bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
            {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Save className="w-4 h-4" strokeWidth={2} />} Simpan Profil
          </button>
        </section>

        <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6" data-testid="profile-email-prefs">
          <div className="flex items-center gap-2 mb-4">
            <Mail className="w-4 h-4 text-cyan-400" strokeWidth={1.5} />
            <h3 className="text-sm uppercase tracking-wider text-white/70">Preferensi Email</h3>
          </div>
          <p className="text-xs text-white/50 mb-4">Pilih kategori email yang ingin Anda terima. Nonaktifkan untuk mengurangi email tanpa mematikan push notifikasi.</p>
          {!prefs ? (
            <div className="flex items-center gap-2 text-white/60 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Memuat...</div>
          ) : (
            <div className="space-y-3">
              {([
                { key: 'signal', label: 'Signal Trading', desc: 'Email tiap ada signal baru dari admin/mentor.' },
                { key: 'broadcast', label: 'Broadcast Admin', desc: 'Pengumuman & info penting dari admin.' },
                { key: 'promo', label: 'Promo & Penawaran', desc: 'Info promo, diskon course/VIP, dan event khusus.' },
              ] as { key: keyof EmailPreferences; label: string; desc: string }[]).map((row) => (
                <label key={row.key} className="flex items-start justify-between gap-4 py-2 border-b border-white/5 last:border-0 cursor-pointer">
                  <div className="flex-1">
                    <div className="text-sm text-white">{row.label}</div>
                    <div className="text-xs text-white/50 mt-0.5">{row.desc}</div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={prefs[row.key]}
                    data-testid={`profile-email-pref-${row.key}`}
                    disabled={savingPrefs}
                    onClick={() => togglePref(row.key)}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${prefs[row.key] ? 'bg-cyan-400' : 'bg-white/10'} ${savingPrefs ? 'opacity-60' : ''}`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-[#050914] transition-transform ${prefs[row.key] ? 'translate-x-5' : 'translate-x-0.5'}`} />
                  </button>
                </label>
              ))}
            </div>
          )}
        </section>

        <section className="bg-white/[0.02] border border-white/10 rounded-2xl p-6">
          <h3 className="text-sm uppercase tracking-wider text-white/70 mb-4">Ubah Password</h3>
          <div className="space-y-3">
            <div><label className={labelClass}>Password Saat Ini</label><input data-testid={PROFILE.currentPw} type="password" className={inputClass} value={currentPw} onChange={(e) => setCurrentPw(e.target.value)} /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className={labelClass}>Password Baru</label><input data-testid={PROFILE.newPw} type="password" className={inputClass} value={newPw} onChange={(e) => setNewPw(e.target.value)} /></div>
              <div><label className={labelClass}>Konfirmasi Password</label><input data-testid={PROFILE.confirmPw} type="password" className={inputClass} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} /></div>
            </div>
          </div>
          <button data-testid={PROFILE.changePwBtn} onClick={savePassword} disabled={savingPw || !currentPw || !newPw || !confirmPw} className="mt-5 bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
            {savingPw ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <KeyRound className="w-4 h-4" strokeWidth={2} />} Ubah Password
          </button>
        </section>
      </div>
    </DashboardLayout>
  );
}
